import React from 'react'
import { useEditableFormStateOptional } from '../contexts/editableForm'
import { useFieldContext, useFormContext } from '../createAppFormHook'
import { valueEquals } from '../utils/valueEquals'

export type EditableOptions<TValue = unknown> = {
  autoExitOnSaved?: boolean
  exitOnBlur?: boolean
  exitOnEnter?: boolean
  undoOnEscape?: boolean
  /**
   * Custom equality comparator. Defaults to valueEquals (deep compare, dayjs-aware).
   */
  equals?: (a: TValue | undefined, b: TValue | undefined) => boolean
  /**
   * Strategy for leaving edit mode when the value equals the originally captured value.
   * - 'onBlur' (default): only exit on blur if the value equals original
   * - 'debounced': exit after no changes for `revertDebounceMs` and values are equal
   * - 'immediate': exit immediately when values are equal (previous behavior)
   */
  revertExit?: 'onBlur' | 'debounced' | 'immediate'
  /** Idle duration used when revertExit = 'debounced' (ms). Default: 600 */
  revertDebounceMs?: number
}

export function useEditableFieldMode<TValue>(name?: string, opts: EditableOptions<TValue> = {}) {
  const policy = useEditableFormStateOptional(s => s.policy)
  const formMeta = useEditableFormStateOptional(s => s.meta)
  const {
    autoExitOnSaved = true,
    exitOnBlur = policy.exitOnBlur,
    exitOnEnter = policy.exitOnEnter,
    undoOnEscape = policy.allowUndo,
    equals = valueEquals as (a: TValue | undefined, b: TValue | undefined) => boolean,
    revertExit = policy.revertExit,
    revertDebounceMs = policy.revertDebounceMs,
  } = opts

  const form = useFormContext()
  const field = useFieldContext<TValue>()
  const fieldName: string = name ?? String(field.name)

  const [editing, setEditing] = React.useState(false)
  const originalRef = React.useRef<TValue | undefined>(undefined)
  const lastComparedRef = React.useRef<TValue | undefined>(undefined)
  const isComposingRef = React.useRef(false)
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const enterEdit = React.useCallback(() => {
    if (policy.forceMode === 'read') return
    originalRef.current = form.getFieldValue(fieldName) as TValue
    setEditing(true)
  }, [policy.forceMode, form, fieldName])

  const exitRead = React.useCallback(() => setEditing(false), [])

  const undoAndExit = React.useCallback(() => {
    form.setFieldValue(fieldName, originalRef.current)
    setEditing(false)
  }, [form, fieldName])

  React.useEffect(() => {
    if (!autoExitOnSaved) return
    if (formMeta.lastSavedAt) setEditing(false)
  }, [formMeta.lastSavedAt, autoExitOnSaved])

  // Equality-based exit strategies while editing
  React.useEffect(() => {
    if (!editing) return

    if (revertExit === 'immediate') {
      const handle = () => {
        if (isComposingRef.current) return
        const current = form.getFieldValue(fieldName) as TValue
        const last = lastComparedRef.current
        if (last !== current) {
          lastComparedRef.current = current
          if (equals(current, originalRef.current)) {
            setEditing(false)
          }
        }
      }
      const unsubscribe = form.store.subscribe(handle)
      handle()
      return unsubscribe
    }

    if (revertExit === 'debounced') {
      const handle = () => {
        if (isComposingRef.current) return
        const current = form.getFieldValue(fieldName) as TValue
        lastComparedRef.current = current
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = setTimeout(() => {
          const now = form.getFieldValue(fieldName) as TValue
          if (equals(now, originalRef.current)) {
            setEditing(false)
          }
        }, revertDebounceMs)
      }
      const unsubscribe = form.store.subscribe(handle)
      return () => {
        unsubscribe()
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          debounceTimerRef.current = null
        }
      }
    }

    // revertExit === 'onBlur' -> no subscription needed
    return
  }, [editing, equals, form, fieldName, revertExit, revertDebounceMs])

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && exitOnEnter) {
        exitRead()
      } else if (e.key === 'Escape' && undoOnEscape) {
        undoAndExit()
      }
    },
    [exitOnEnter, undoOnEscape, exitRead, undoAndExit],
  )

  const onBlur = React.useCallback(() => {
    field.handleBlur()
    if (revertExit === 'onBlur') {
      const current = form.getFieldValue(fieldName) as TValue
      if (equals(current, originalRef.current)) {
        setEditing(false)
        return
      }
    }
    if (exitOnBlur) exitRead()
  }, [field, form, fieldName, exitOnBlur, exitRead, equals, revertExit])

  const onCompositionStart = React.useCallback(() => {
    isComposingRef.current = true
  }, [])

  const onCompositionEnd = React.useCallback(() => {
    isComposingRef.current = false
  }, [])

  const mode: 'read' | 'edit' = policy.forceMode ?? (editing ? 'edit' : 'read')
  const guardedUndo = React.useCallback(() => {
    if (!policy.allowUndo) return
    undoAndExit()
  }, [policy.allowUndo, undoAndExit])

  return {
    mode,
    editing,
    canUndo: policy.allowUndo,
    enterEdit,
    exitRead,
    undoAndExit: guardedUndo,
    onKeyDown,
    onBlur,
    onCompositionStart,
    onCompositionEnd,
  }
}
